import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Copy, ArrowUpRight, Terminal, Key, Lightning, ShieldCheck, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { TOOLS } from "../lib/tools";

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const SECTIONS = [
  { id: "quickstart", label: "Quickstart" },
  { id: "authentication", label: "Authentication" },
  { id: "plans", label: "Plans & rate limits" },
  { id: "generate", label: "POST /api/generate" },
  { id: "tools", label: "Tool reference" },
  { id: "usage", label: "GET /api/me/usage" },
  { id: "audit", label: "Audit endpoints" },
  { id: "errors", label: "Error codes" },
  { id: "sdks", label: "Code samples" },
];

function copy(text, label = "Copied") {
  navigator.clipboard.writeText(text);
  toast.success(label);
}

function CodeBlock({ children, lang = "bash" }) {
  return (
    <div className="relative group my-4">
      <div className="absolute top-2 right-2 flex items-center gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#737373] bg-white px-1.5 py-0.5">
          {lang}
        </span>
        <button
          onClick={() => copy(children)}
          className="bg-white border border-[#E5E7EB] p-1.5 hover:bg-[#F5F5F5]"
          aria-label="Copy code"
        >
          <Copy size={12} weight="bold" />
        </button>
      </div>
      <pre className="bg-[#0A0A0A] text-[#F5F5F5] p-5 overflow-x-auto text-[13px] font-mono leading-relaxed">
        <code>{children}</code>
      </pre>
    </div>
  );
}

export default function ApiDocs() {
  const [activeTool, setActiveTool] = useState(TOOLS[0].key);
  const currentTool = useMemo(() => TOOLS.find((t) => t.key === activeTool) || TOOLS[0], [activeTool]);

  const curlExample = `curl -X POST ${API_BASE}/api/generate \\
  -H "Authorization: ApiKey ord_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '${JSON.stringify(
    {
      tool: currentTool.key,
      inputs: Object.fromEntries(
        currentTool.fields.map((f) => [f.name, f.placeholder || "…"])
      ),
    },
    null,
    2
  ).replace(/\n/g, "\n      ")}'`;

  const jsExample = `const res = await fetch("${API_BASE}/api/generate", {
  method: "POST",
  headers: {
    "Authorization": "ApiKey ord_your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    tool: "${currentTool.key}",
    inputs: ${JSON.stringify(
      Object.fromEntries(currentTool.fields.map((f) => [f.name, f.placeholder || "…"])),
      null,
      2
    ).replace(/\n/g, "\n    ")},
  }),
});
const data = await res.json();
console.log(data.output);`;

  const pythonExample = `import requests

res = requests.post(
    "${API_BASE}/api/generate",
    headers={
        "Authorization": "ApiKey ord_your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "tool": "${currentTool.key}",
        "inputs": ${JSON.stringify(
          Object.fromEntries(currentTool.fields.map((f) => [f.name, f.placeholder || "…"])),
          null,
          8
        ).replace(/\n/g, "\n        ")},
    },
)
print(res.json()["output"])`;

  return (
    <div data-testid="api-docs-page">
      <section className="border-b border-[#E5E7EB]">
        <div className="container-wide py-10 md:py-14">
          <p className="font-mono text-xs uppercase tracking-[0.22em] text-[#002FA7] mb-3">API Reference</p>
          <h1 className="font-display text-4xl sm:text-5xl md:text-6xl font-black tracking-tighter">
            Ordeneli API
          </h1>
          <p className="mt-4 text-[#525252] text-base sm:text-lg max-w-2xl">
            Call every marketing tool programmatically. Same plan limits apply.
            Available on the <strong>Pro plan</strong>.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/settings?tab=api" className="btn-primary" data-testid="docs-get-key-cta">
              <Key size={16} weight="bold" /> Get your API key
            </Link>
            <Link to="/pricing" className="btn-secondary">
              See pricing <ArrowUpRight size={16} weight="bold" />
            </Link>
          </div>
        </div>
      </section>

      <section className="container-wide py-10 md:py-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Sidebar TOC */}
        <aside className="lg:col-span-3 order-2 lg:order-1">
          <nav className="lg:sticky lg:top-24 border-l border-[#E5E7EB]" data-testid="docs-toc">
            {SECTIONS.map((s) => (
              <a
                key={s.id}
                href={`#${s.id}`}
                className="block pl-4 py-2 -ml-px border-l-2 border-transparent text-sm text-[#525252] hover:text-[#0A0A0A] hover:border-[#0A0A0A] transition-colors"
              >
                {s.label}
              </a>
            ))}
          </nav>
        </aside>

        {/* Main content */}
        <main className="lg:col-span-9 order-1 lg:order-2 space-y-14">
          {/* QUICKSTART */}
          <section id="quickstart">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
              <Lightning size={24} weight="duotone" className="text-[#002FA7]" />
              Quickstart
            </h2>
            <p className="mt-3 text-[#525252]">Three steps to your first generation:</p>
            <ol className="mt-4 space-y-3 list-decimal list-inside text-[#0A0A0A]">
              <li>Sign in and upgrade to the <Link to="/pricing" className="text-[#002FA7] font-semibold">Pro plan</Link>.</li>
              <li>Create an API key at <Link to="/settings?tab=api" className="text-[#002FA7] font-semibold">Settings → API Keys</Link>. Copy it — it's shown only once.</li>
              <li>Make a request with the <code className="bg-[#F5F5F5] px-1.5 py-0.5 font-mono text-sm">Authorization: ApiKey</code> header.</li>
            </ol>
            <CodeBlock lang="bash">{`curl -X POST ${API_BASE}/api/generate \\
  -H "Authorization: ApiKey ord_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"tool":"seo","inputs":{"website":"acme.com","topic":"organic skincare"}}'`}</CodeBlock>
          </section>

          {/* AUTH */}
          <section id="authentication">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
              <ShieldCheck size={24} weight="duotone" className="text-[#002FA7]" />
              Authentication
            </h2>
            <p className="mt-3 text-[#525252]">
              Every request must include your API key in the <code className="bg-[#F5F5F5] px-1.5 py-0.5 font-mono text-sm">Authorization</code> header using the <code className="bg-[#F5F5F5] px-1.5 py-0.5 font-mono text-sm">ApiKey</code> scheme.
            </p>
            <CodeBlock lang="http">{`Authorization: ApiKey ord_xxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx`}</CodeBlock>
            <div className="mt-4 border border-[#FECACA] bg-[#FEF2F2] p-4 text-sm flex gap-3">
              <Warning size={18} weight="bold" className="text-[#DC2626] shrink-0 mt-0.5" />
              <div>
                <strong className="text-[#0A0A0A]">Keep your keys secret.</strong>
                <p className="text-[#525252] mt-1">Never commit them to git or ship them in client-side bundles. Revoke compromised keys immediately from <Link to="/settings?tab=api" className="text-[#002FA7]">Settings → API Keys</Link>.</p>
              </div>
            </div>
          </section>

          {/* PLANS */}
          <section id="plans">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Plans & rate limits</h2>
            <div className="mt-4 border border-[#E5E7EB] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5]">
                  <tr>
                    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Plan</th>
                    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Tools</th>
                    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Monthly cap</th>
                    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">API access</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  <tr><td className="px-4 py-3 font-semibold">Starter</td><td className="px-4 py-3">3 free</td><td className="px-4 py-3">50 / mo</td><td className="px-4 py-3 text-[#DC2626]">✗</td></tr>
                  <tr><td className="px-4 py-3 font-semibold">Growth</td><td className="px-4 py-3">All 10</td><td className="px-4 py-3">2,000 / mo</td><td className="px-4 py-3 text-[#DC2626]">✗</td></tr>
                  <tr className="bg-[#F5F5F5]"><td className="px-4 py-3 font-semibold">Pro</td><td className="px-4 py-3">All 10</td><td className="px-4 py-3">Unlimited</td><td className="px-4 py-3 text-[#16A34A]">✓</td></tr>
                </tbody>
              </table>
            </div>
            <p className="mt-3 text-sm text-[#525252]">API requests count toward your monthly cap. Check remaining quota at <code className="bg-[#F5F5F5] px-1.5 py-0.5 font-mono text-xs">GET /api/me/usage</code>.</p>
          </section>

          {/* GENERATE */}
          <section id="generate">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight flex items-center gap-3">
              <Terminal size={24} weight="duotone" className="text-[#002FA7]" />
              POST <code className="font-mono">/api/generate</code>
            </h2>
            <p className="mt-3 text-[#525252]">Runs any tool. Returns markdown output you can render or post-process.</p>

            <h3 className="mt-6 font-display text-lg font-bold">Request body</h3>
            <div className="mt-2 border border-[#E5E7EB] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5]">
                  <tr><th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Field</th><th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Type</th><th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Description</th></tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  <tr><td className="px-4 py-3 font-mono">tool</td><td className="px-4 py-3 font-mono text-[#737373]">string</td><td className="px-4 py-3">Tool key — see the list below</td></tr>
                  <tr><td className="px-4 py-3 font-mono">inputs</td><td className="px-4 py-3 font-mono text-[#737373]">object</td><td className="px-4 py-3">Tool-specific fields — see reference</td></tr>
                </tbody>
              </table>
            </div>

            <h3 className="mt-6 font-display text-lg font-bold">Response</h3>
            <CodeBlock lang="json">{`{
  "id": "b5d3c0b5-…",
  "tool": "seo",
  "output": "## Primary keyword\\n- organic skincare for men …",
  "created_at": "2026-04-17T12:00:00.000Z"
}`}</CodeBlock>
          </section>

          {/* TOOL REFERENCE */}
          <section id="tools">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Tool reference</h2>
            <p className="mt-3 text-[#525252]">Click a tool to see its input fields and a live request sample.</p>

            <div className="mt-6 flex flex-wrap gap-2" data-testid="tool-picker">
              {TOOLS.map((t) => (
                <button
                  key={t.key}
                  onClick={() => setActiveTool(t.key)}
                  className={`px-3 py-1.5 text-xs font-mono uppercase tracking-[0.14em] border transition-colors ${
                    activeTool === t.key
                      ? "border-[#002FA7] bg-[#002FA7] text-white"
                      : "border-[#E5E7EB] text-[#525252] hover:border-[#0A0A0A]"
                  }`}
                  data-testid={`docs-tool-${t.key}`}
                >
                  {t.key}
                  {t.free && <span className="ml-1.5 text-[9px] opacity-80">· free</span>}
                </button>
              ))}
            </div>

            <div className="mt-6 border border-[#E5E7EB] p-6">
              <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                  <h3 className="font-display text-xl font-bold">{currentTool.name}</h3>
                  <p className="text-sm text-[#525252] mt-1">{currentTool.tagline}</p>
                </div>
                <span className={`inline-flex items-center px-2 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.14em] ${currentTool.free ? "bg-[#002FA7] text-white" : "border border-[#0A0A0A] text-[#0A0A0A]"}`}>
                  {currentTool.free ? "Free" : "Pro"}
                </span>
              </div>

              <h4 className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Inputs</h4>
              <ul className="mt-2 divide-y divide-[#E5E7EB] border border-[#E5E7EB]">
                {currentTool.fields.map((f) => (
                  <li key={f.name} className="px-4 py-3 flex items-start justify-between gap-4 flex-wrap text-sm">
                    <div className="flex-1 min-w-[200px]">
                      <code className="font-mono font-semibold">{f.name}</code>
                      {f.required && <span className="ml-2 text-[#DC2626] text-xs font-bold">required</span>}
                      <span className="ml-2 text-[#737373] text-xs">{f.type === "textarea" ? "long text" : "string"}</span>
                    </div>
                    <span className="text-[#525252]">{f.label}</span>
                  </li>
                ))}
              </ul>

              <h4 className="mt-5 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">cURL example</h4>
              <CodeBlock lang="bash">{curlExample}</CodeBlock>
            </div>
          </section>

          {/* USAGE */}
          <section id="usage">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">GET <code className="font-mono">/api/me/usage</code></h2>
            <p className="mt-3 text-[#525252]">Check your current plan, generations used, remaining quota, and allowed tools.</p>
            <CodeBlock lang="json">{`{
  "plan": "pro",
  "month": "2026-04",
  "used": 42,
  "cap": null,
  "tools_allowed": ["seo","ads","social","reviews","local","brand","starter","formation","growth","relate","source"],
  "free_tools": ["seo","ads","starter"],
  "api_access": true,
  "seats": { "used": 1, "allowed": 3 }
}`}</CodeBlock>
            <p className="mt-2 text-sm text-[#737373]"><code className="font-mono">cap: null</code> means unlimited (Pro plan).</p>
          </section>

          {/* AUDIT */}
          <section id="audit">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Audit endpoints</h2>
            <p className="mt-3 text-[#525252]">The public audit flow — no API key required. Useful for lead-gen widgets on your own sites.</p>
            <div className="mt-4 space-y-4">
              <div className="border border-[#E5E7EB] p-4">
                <p className="font-mono text-sm"><span className="text-[#002FA7] font-bold">POST</span> /api/audit/start</p>
                <p className="text-xs text-[#737373] mt-1">Body: <code className="font-mono">{`{"url":"acme.com","topic":"optional"}`}</code> → returns <code className="font-mono">{`{id, partial, url}`}</code></p>
              </div>
              <div className="border border-[#E5E7EB] p-4">
                <p className="font-mono text-sm"><span className="text-[#002FA7] font-bold">POST</span> /api/audit/{`{id}`}/unlock</p>
                <p className="text-xs text-[#737373] mt-1">Body: <code className="font-mono">{`{"email":"lead@example.com"}`}</code> → returns <code className="font-mono">{`{full, email_sent}`}</code></p>
              </div>
              <div className="border border-[#E5E7EB] p-4">
                <p className="font-mono text-sm"><span className="text-[#002FA7] font-bold">GET</span> /api/audit/{`{id}`}/og</p>
                <p className="text-xs text-[#737373] mt-1">Server-rendered HTML with OG/Twitter meta tags. Share this URL on social platforms for rich previews.</p>
              </div>
            </div>
          </section>

          {/* ERRORS */}
          <section id="errors">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Error codes</h2>
            <div className="mt-4 border border-[#E5E7EB] overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-[#F5F5F5]">
                  <tr>
                    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Status</th>
                    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Error</th>
                    <th className="text-left px-4 py-3 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Meaning</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E5E7EB]">
                  <tr><td className="px-4 py-3 font-mono">400</td><td className="px-4 py-3 font-mono text-[#737373]">Unknown tool</td><td className="px-4 py-3">The <code className="font-mono">tool</code> key is invalid.</td></tr>
                  <tr><td className="px-4 py-3 font-mono">401</td><td className="px-4 py-3 font-mono text-[#737373]">Authentication required</td><td className="px-4 py-3">Missing or invalid API key.</td></tr>
                  <tr><td className="px-4 py-3 font-mono">403</td><td className="px-4 py-3 font-mono text-[#737373]">plan_gate</td><td className="px-4 py-3">Tool requires Growth or Pro — you're on Starter.</td></tr>
                  <tr><td className="px-4 py-3 font-mono">403</td><td className="px-4 py-3 font-mono text-[#737373]">usage_cap</td><td className="px-4 py-3">Monthly generation limit reached.</td></tr>
                  <tr><td className="px-4 py-3 font-mono">500</td><td className="px-4 py-3 font-mono text-[#737373]">LLM error</td><td className="px-4 py-3">Upstream model error — safe to retry.</td></tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* SAMPLES */}
          <section id="sdks">
            <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Code samples</h2>
            <p className="mt-3 text-[#525252]">Drop these into your project. Replace the key with your own from <Link to="/settings?tab=api" className="text-[#002FA7] font-semibold">Settings → API Keys</Link>.</p>

            <h3 className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">JavaScript (fetch)</h3>
            <CodeBlock lang="javascript">{jsExample}</CodeBlock>

            <h3 className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Python</h3>
            <CodeBlock lang="python">{pythonExample}</CodeBlock>

            <h3 className="mt-6 font-mono text-[11px] uppercase tracking-[0.18em] text-[#737373]">Zapier / Make / n8n</h3>
            <p className="text-[#525252] text-sm">
              Use a <strong>Webhooks → POST</strong> step pointing at <code className="font-mono bg-[#F5F5F5] px-1.5 py-0.5 text-xs">{API_BASE}/api/generate</code>.
              Add the <code className="font-mono bg-[#F5F5F5] px-1.5 py-0.5 text-xs">Authorization</code> header with value <code className="font-mono bg-[#F5F5F5] px-1.5 py-0.5 text-xs">ApiKey ord_…</code>.
              Map inputs from the prior step.
            </p>
          </section>

          {/* FOOTER CTA */}
          <section className="border-t border-[#E5E7EB] pt-10">
            <div className="border border-[#0A0A0A] bg-[#0A0A0A] text-white p-8 md:p-12">
              <h3 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight">Ready to build?</h3>
              <p className="mt-3 text-white/75 max-w-xl">Upgrade to Pro and get unlimited generations + API access + 3 team seats.</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <Link to="/pricing" className="btn-primary bg-white text-[#0A0A0A] hover:bg-[#F5F5F5]">
                  See pricing <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link to="/settings?tab=api" className="btn-secondary border-white text-white hover:bg-white hover:text-[#0A0A0A]">
                  Get your key
                </Link>
              </div>
            </div>
          </section>
        </main>
      </section>
    </div>
  );
}
